import { isToolUIPart } from "ai";
import type { AgentUIMessage } from "@/lib/ai/types";

const partRenderSignatureCache = new WeakMap<object, string>();
const outputRenderDigests = new WeakMap<object, string>();

function appendDigestToken(hash: number, value: string): number {
  let nextHash = hash;
  for (let index = 0; index < value.length; index += 1) {
    nextHash ^= value.charCodeAt(index);
    nextHash = Math.imul(nextHash, 16_777_619);
  }
  return nextHash;
}

function appendValueDigest(
  hash: number,
  value: unknown,
  visited: WeakSet<object>,
): number {
  if (value === null) {
    return appendDigestToken(hash, "null;");
  }

  if (typeof value !== "object") {
    return appendDigestToken(hash, `${typeof value}:${String(value)};`);
  }

  if (visited.has(value)) {
    return appendDigestToken(hash, "circular;");
  }
  visited.add(value);

  if (Array.isArray(value)) {
    let nextHash = appendDigestToken(hash, `array:${value.length}[`);
    for (const item of value) {
      nextHash = appendValueDigest(nextHash, item, visited);
    }
    return appendDigestToken(nextHash, "];");
  }

  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  let nextHash = appendDigestToken(hash, `object:${keys.length}{`);
  for (const key of keys) {
    nextHash = appendDigestToken(nextHash, `key:${key}=`);
    nextHash = appendValueDigest(nextHash, record[key], visited);
  }
  return appendDigestToken(nextHash, "};");
}

function getOutputRenderDigest(output: unknown): string {
  if (typeof output === "object" && output !== null) {
    const cachedDigest = outputRenderDigests.get(output);
    if (cachedDigest) {
      return cachedDigest;
    }

    const hash = appendValueDigest(2_166_136_261, output, new WeakSet());
    const digest = (hash >>> 0).toString(36);
    outputRenderDigests.set(output, digest);
    return digest;
  }

  const hash = appendValueDigest(2_166_136_261, output, new WeakSet());
  return (hash >>> 0).toString(36);
}

/**
 * message part の描画結果に影響する値だけを安定した署名へ変換する。
 */
export function getMessagePartRenderSignature(
  part: AgentUIMessage["parts"][number],
): string {
  if (typeof part !== "object" || part === null) {
    return JSON.stringify(part);
  }

  const cachedSignature = partRenderSignatureCache.get(part);
  if (cachedSignature) {
    return cachedSignature;
  }

  let signature: string;
  if (part.type === "text" || part.type === "reasoning") {
    signature = JSON.stringify({
      type: part.type,
      text: part.text ?? "",
    });
  } else if (isToolUIPart(part)) {
    // 大容量結果そのものは直列化せず、bounded digest で同じ terminal state 内の
    // 内容置換も検出する。同一 object の digest は WeakMap で再利用する。
    const dynamicToolName =
      part.type === "dynamic-tool" ? part.toolName : undefined;
    const mutableInput =
      part.state === "input-streaming" ? (part.input ?? null) : undefined;
    const approval =
      part.state === "approval-requested" ||
      part.state === "approval-responded" ||
      part.state === "output-denied"
        ? part.approval
        : undefined;

    signature = JSON.stringify({
      type: part.type,
      toolName: dynamicToolName,
      toolCallId: part.toolCallId ?? null,
      state: part.state,
      input: mutableInput,
      approval,
      preliminary:
        part.state === "output-available" ? part.preliminary : undefined,
      outputVersion:
        part.state === "output-available"
          ? getOutputRenderDigest(part.output)
          : undefined,
      errorText: part.state === "output-error" ? part.errorText : undefined,
    });
  } else {
    signature = JSON.stringify(part);
  }

  partRenderSignatureCache.set(part, signature);
  return signature;
}
